import {
  BillingAddress,
  DeliveryAddress,
  EnrollmentForm,
  EntityKind
} from '../types/enrollmentForm'
import { PlanetbTokenPayload } from '../types/planetb'

const ENTITY_KINDS: EntityKind[] = ['CSE', 'Entreprise', 'Association', 'Collectivité', 'Autre']

const CONTACT_ROLES = [
  'Trésorier(e)',
  'Secrétaire',
  'Chef d\'entreprise',
  'Responsable financier(e)',
  'Responsable marketing',
  'Autre'
]

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  france: 'FR',
  belgique: 'BE',
  belgium: 'BE',
  luxembourg: 'LU',
  suisse: 'CH',
  switzerland: 'CH',
  fr: 'FR',
  be: 'BE',
  lu: 'LU',
  ch: 'CH'
}

export interface PlanetbFormValues {
  companyName: string
  entityKind: EntityKind
  entityKindOther: string
  siret: string
  siren: string
  vatNumber: string
  rcs: string
  nafCode: string
  employeeRange: string
  adminFullName: string
  contactRole: string
  contactRoleOther: string
  adminEmail: string
  mobilePhone: string
  landlinePhone: string
  billingAddress: BillingAddress
  deliveryAddresses: DeliveryAddress[]
}

function isEmpty(value: string | undefined | null): boolean {
  return value == null || value.trim() === ''
}

export function mapCountryName(countryName?: string): string {
  if (!countryName) return 'FR'
  const normalized = countryName.trim().toLowerCase()
  return COUNTRY_NAME_TO_CODE[normalized] || 'FR'
}

export function mapEmployeeCount(count?: number): string {
  if (count == null || count <= 0) return ''
  if (count <= 10) return '1-10'
  if (count <= 50) return '11-50'
  if (count <= 250) return '51-250'
  return '250+'
}

export function mapEntityKind(entityKind?: string): { entityKind: EntityKind; entityKindOther: string } {
  if (!entityKind) return { entityKind: '', entityKindOther: '' }
  if (ENTITY_KINDS.includes(entityKind as EntityKind) && entityKind !== 'Autre') {
    return { entityKind: entityKind as EntityKind, entityKindOther: '' }
  }
  return { entityKind: 'Autre', entityKindOther: entityKind }
}

export function mapContactRole(role?: string): { contactRole: string; contactRoleOther: string } {
  if (!role) return { contactRole: '', contactRoleOther: '' }
  if (CONTACT_ROLES.includes(role) && role !== 'Autre') {
    return { contactRole: role, contactRoleOther: '' }
  }
  return { contactRole: 'Autre', contactRoleOther: role }
}

export function buildAdminFullName(firstname?: string, lastname?: string): string {
  return [firstname, lastname].filter(part => !isEmpty(part)).join(' ').trim()
}

function mapBillingAddress(address?: PlanetbTokenPayload['billing_address']): BillingAddress {
  return {
    number: address?.address_number?.trim() || '',
    street: address?.street_name?.trim() || '',
    city: address?.city_name?.trim() || '',
    zipcode: address?.zip_code?.trim() || '',
    country: mapCountryName(address?.country_name)
  }
}

function mapDeliveryAddresses(addresses?: PlanetbTokenPayload['delivery_address']): DeliveryAddress[] {
  if (!addresses?.length) return []

  return addresses.map((address, index) => ({
    id: `planetb_${index}_${Date.now()}`,
    siteName: address.name?.trim() || '',
    street: address.street_name?.trim() || '',
    city: address.city_name?.trim() || '',
    zipcode: address.zip_code?.trim() || '',
    country: mapCountryName(address.country_name),
    prefilled: true
  }))
}

export function mapPlanetbPayloadToFormValues(payload: PlanetbTokenPayload): PlanetbFormValues {
  const { entityKind, entityKindOther } = mapEntityKind(payload.entity_kind)
  const { contactRole, contactRoleOther } = mapContactRole(payload.main_contact?.role)
  const mobilePhone = payload.mobile_number?.trim() || ''
  const landlinePhone = payload.landline_phone?.trim() || ''

  return {
    companyName: payload.name?.trim() || '',
    entityKind,
    entityKindOther,
    siret: payload.establishment_identifier?.trim() || '',
    siren: payload.business_registration_number?.trim() || '',
    vatNumber: payload.intra_vat_number?.trim() || '',
    rcs: payload.company_registry?.trim() || '',
    nafCode: payload.business_activity_code?.trim() || '',
    employeeRange: mapEmployeeCount(payload.employee_count),
    adminFullName: buildAdminFullName(payload.main_contact?.firstname, payload.main_contact?.lastname),
    contactRole,
    contactRoleOther,
    adminEmail: payload.email?.trim() || '',
    mobilePhone: mobilePhone || landlinePhone,
    landlinePhone: mobilePhone ? landlinePhone : '',
    billingAddress: mapBillingAddress(payload.billing_address),
    deliveryAddresses: mapDeliveryAddresses(payload.delivery_address)
  }
}

function applyStringField(
  form: EnrollmentForm,
  field: keyof EnrollmentForm,
  value: string,
  prefilledFields: string[]
): void {
  const current = form[field]
  if (typeof current !== 'string' || !isEmpty(current) || isEmpty(value)) return
  ;(form[field] as string) = value
  prefilledFields.push(field as string)
}

function applyBillingAddress(
  form: EnrollmentForm,
  source: BillingAddress,
  prefilledFields: string[]
): void {
  const fields: Array<keyof BillingAddress> = ['number', 'street', 'city', 'zipcode', 'country']

  fields.forEach(field => {
    if (!isEmpty(form.billingAddress[field]) || isEmpty(source[field])) return
    form.billingAddress[field] = source[field]
    prefilledFields.push(`billingAddress.${field}`)
  })
}

export function applyPlanetbPrefill(form: EnrollmentForm, values: PlanetbFormValues): string[] {
  const prefilledFields: string[] = []

  applyStringField(form, 'companyName', values.companyName, prefilledFields)
  applyStringField(form, 'entityKind', values.entityKind, prefilledFields)
  applyStringField(form, 'entityKindOther', values.entityKindOther, prefilledFields)
  applyStringField(form, 'siret', values.siret, prefilledFields)
  applyStringField(form, 'siren', values.siren, prefilledFields)
  applyStringField(form, 'vatNumber', values.vatNumber, prefilledFields)
  applyStringField(form, 'rcs', values.rcs, prefilledFields)
  applyStringField(form, 'nafCode', values.nafCode, prefilledFields)
  applyStringField(form, 'employeeRange', values.employeeRange, prefilledFields)
  applyStringField(form, 'adminFullName', values.adminFullName, prefilledFields)
  applyStringField(form, 'contactRole', values.contactRole, prefilledFields)
  applyStringField(form, 'contactRoleOther', values.contactRoleOther, prefilledFields)
  applyStringField(form, 'adminEmail', values.adminEmail, prefilledFields)
  applyStringField(form, 'mobilePhone', values.mobilePhone, prefilledFields)
  applyStringField(form, 'landlinePhone', values.landlinePhone, prefilledFields)

  applyBillingAddress(form, values.billingAddress, prefilledFields)

  if (form.deliveryAddresses.length === 0 && values.deliveryAddresses.length > 0) {
    form.deliveryAddresses = values.deliveryAddresses.map(address => ({ ...address }))
    prefilledFields.push('deliveryAddresses')
  }

  return prefilledFields
}
